import React, { useEffect, useRef } from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { useUIStore } from '@/store/useUIStore';
import { bus } from '@/store/eventBus';
import { PixiApp } from '@/pixi/PixiApp';

export const FloorMapCanvas: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pixiRef = useRef<PixiApp | null>(null);
  const rooms = useSimulationStore(s => s.rooms);
  const agents = useSimulationStore(s => s.agents);
  const { selectAgent } = useUIStore();

  // Initialisation Pixi (une seule fois)
  useEffect(() => {
    if (!containerRef.current) return;
    const pixiApp = new PixiApp();
    pixiRef.current = pixiApp;

    pixiApp.init(containerRef.current, rooms, agents).catch(console.error);

    // Écoute les clics agents émis par le canvas Pixi
    const handler = ({ agentId }: { agentId: string }) => selectAgent(agentId);
    bus.on('agent:click', handler);

    return () => {
      bus.off('agent:click', handler);
      pixiRef.current?.destroy();
      pixiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render du plan UNIQUEMENT si la structure des salles change (évite le flicker
  // car le bridge live recharge tout à chaque update agent)
  const roomsSigRef = useRef('');
  useEffect(() => {
    const sig = rooms.map(r => `${r.id}:${r.label}:${r.color}:${r.storeId}`).join('|');
    if (sig !== roomsSigRef.current) {
      roomsSigRef.current = sig;
      pixiRef.current?.refreshFloorMap(rooms);
    }
  }, [rooms]);

  // Synchroniser les agents à chaque changement de state
  useEffect(() => {
    pixiRef.current?.syncAgents(agents);
  }, [agents]);

  return (
    <div className="relative flex-1 overflow-hidden bg-bg-base flex items-center justify-center">
      {/* CRT overlay en CSS */}
      <div
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px)',
        }}
      />
      {/* Vignette corners */}
      <div
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.5) 100%)',
        }}
      />
      {/* Canvas container */}
      <div
        ref={containerRef}
        className="relative"
        style={{ imageRendering: 'pixelated' }}
      />
    </div>
  );
};
